import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/context/NotificationContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Sneaker, Heart, UserCircle, SignOut, ChartLineUp, List, X, Scales, BellRinging, Bell, CaretDown, CurrencyDollar, Sparkle } from '@phosphor-icons/react';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currencySymbols = { USD: '$', GBP: '\u00A3', AUD: 'A$' };

export default function Navbar() {
  const { user, logout } = useAuth();
  const notif = useNotifications();
  const { currency, setCurrency: changeCurrency, currencies, labels } = useCurrency();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropCount, setDropCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/price-drops/count`).then(({ data }) => setDropCount(data.count || 0)).catch(() => {});
    const interval = setInterval(() => {
      axios.get(`${API}/price-drops/count`).then(({ data }) => setDropCount(data.count || 0)).catch(() => {});
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  // Fetch wishlist count for logged-in users
  useEffect(() => {
    if (user) {
      const fetchWl = () => {
        axios.get(`${API}/wishlist`, { withCredentials: true })
          .then(({ data }) => setWishlistCount(Array.isArray(data) ? data.length : 0))
          .catch(() => setWishlistCount(0));
      };
      fetchWl();
      const interval = setInterval(fetchWl, 30000);
      // Listen for instant updates from ProductPage
      const onWlUpdate = () => setTimeout(fetchWl, 500);
      window.addEventListener('wishlist-updated', onWlUpdate);
      return () => { clearInterval(interval); window.removeEventListener('wishlist-updated', onWlUpdate); };
    } else {
      setWishlistCount(0);
    }
  }, [user]);

  // Close bell dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const unreadCount = notif?.unreadCount || 0;
  const notifications = notif?.notifications || [];

  return (
    <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-[#E4E4E7] backdrop-saturate-150 py-4 px-6 md:px-12" data-testid="navbar">
      <div className="flex justify-between items-center max-w-[1400px] mx-auto">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <Sneaker size={28} weight="duotone" className="text-[#FF3B30]" />
          <span className="font-heading font-black text-xl tracking-tighter uppercase text-[#0A0A0A]">SOLEFINDER</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/quiz" className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] transition-colors" data-testid="nav-quiz">Find Your Fit</Link>
          <Link to="/browse" className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] transition-colors" data-testid="nav-browse">Browse</Link>
          <Link to="/compare" className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] transition-colors flex items-center gap-1" data-testid="nav-compare">
            <Scales size={16} weight="duotone" /> Compare
          </Link>
          <Link to="/price-drops" className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] transition-colors flex items-center gap-1 relative" data-testid="nav-price-drops">
            <BellRinging size={16} weight="duotone" /> Deals
            {dropCount > 0 && (
              <span className="absolute -top-2 -right-4 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="price-drop-badge">
                {dropCount > 99 ? '99+' : dropCount}
              </span>
            )}
          </Link>

          {/* Real-time notification bell */}
          {user && (
            <div className="relative" ref={bellRef}>
              <button onClick={() => setBellOpen(!bellOpen)}
                className="relative text-[#52525B] hover:text-[#0A0A0A] transition-colors p-1"
                data-testid="notification-bell">
                <Bell size={20} weight={unreadCount > 0 ? "fill" : "duotone"} className={unreadCount > 0 ? "text-[#FF3B30]" : ""} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5" data-testid="notification-count">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {bellOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border-2 border-[#0A0A0A] shadow-xl z-50 max-h-[400px] flex flex-col" data-testid="notification-dropdown">
                  <div className="flex justify-between items-center px-4 py-3 border-b border-[#E4E4E7]">
                    <span className="text-xs font-bold uppercase tracking-wide">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={() => notif?.markAllRead()} className="text-xs font-bold text-[#FF3B30] uppercase hover:text-[#D62828]" data-testid="mark-all-read">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-[#A1A1AA]">
                        No notifications yet. Wishlist shoes to get price drop alerts!
                      </div>
                    ) : (
                      notifications.slice(0, 15).map((n, i) => (
                        <div key={n.alert_id || i}
                          className={`px-4 py-3 border-b border-[#F4F4F5] hover:bg-[#F4F4F5] cursor-pointer transition-colors ${!n.read ? 'bg-red-50/50' : ''}`}
                          onClick={() => {
                            if (n.alert_id && !n.read) notif?.markRead(n.alert_id);
                            if (n.shoe_id) navigate(`/shoe/${n.shoe_id}`);
                            setBellOpen(false);
                          }}
                          data-testid={`notification-item-${i}`}>
                          {n.type === 'price_drop' && (
                            <>
                              <p className="text-xs font-bold text-[#FF3B30] uppercase">Price Drop</p>
                              <p className="text-sm font-bold text-[#0A0A0A] mt-0.5">{n.shoe_name}</p>
                              <p className="text-xs text-[#52525B] mt-0.5">
                                {currencySymbols[n.currency] || '$'}{n.new_price?.toFixed(2)} at {n.store_name}
                                <span className="text-[#10B981] font-bold ml-1">({n.drop_pct}% off)</span>
                              </p>
                            </>
                          )}
                          {n.type === 'new_product' && (
                            <>
                              <p className="text-xs font-bold text-[#3B82F6] uppercase">New Arrival</p>
                              <p className="text-sm font-bold text-[#0A0A0A] mt-0.5">{n.brand} {n.model}</p>
                              <p className="text-xs text-[#52525B] mt-0.5">{n.category} — ${n.price_range}</p>
                            </>
                          )}
                          {!n.read && <span className="inline-block w-2 h-2 bg-[#FF3B30] rounded-full mt-1" />}
                        </div>
                      ))
                    )}
                  </div>
                  <Link to="/price-drops" onClick={() => setBellOpen(false)}
                    className="block text-center px-4 py-3 text-xs font-bold uppercase tracking-wide text-[#FF3B30] border-t border-[#E4E4E7] hover:bg-[#F4F4F5] transition-colors"
                    data-testid="view-all-notifications">
                    View All Deals
                  </Link>
                </div>
              )}
            </div>
          )}

          {user && (
            <Link to="/profile" className="text-sm font-bold uppercase tracking-wide text-[#FF3B30] hover:text-[#D62828] transition-colors flex items-center gap-1" data-testid="nav-profile">
              <Sparkle size={14} weight="fill" /> My Profile
            </Link>
          )}

          {user && (
            <Link to="/wishlist" className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] transition-colors flex items-center gap-1 relative" data-testid="nav-wishlist">
              <Heart size={16} weight="duotone" /> Wishlist
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-4 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="wishlist-count-badge">
                  {wishlistCount > 99 ? '99+' : wishlistCount}
                </span>
              )}
            </Link>
          )}
          {user?.role === 'admin' && (
            <Link to="/admin" className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] transition-colors flex items-center gap-1" data-testid="nav-admin">
              <ChartLineUp size={16} weight="duotone" /> Dashboard
            </Link>
          )}

          {/* Currency Selector */}
          <select
            value={currency}
            onChange={(e) => changeCurrency(e.target.value)}
            className="border-2 border-[#E4E4E7] bg-white text-xs font-bold uppercase tracking-wide px-2 py-1.5 cursor-pointer hover:border-[#0A0A0A] transition-colors focus:outline-none focus:border-[#0A0A0A]"
            data-testid="currency-selector">
            {currencies.map(c => (
              <option key={c} value={c}>{labels[c]}</option>
            ))}
          </select>

          {user ? (
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#52525B]" data-testid="nav-user-name">{user.name || user.email}</span>
              <button onClick={handleLogout} className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors flex items-center gap-1" data-testid="nav-logout">
                <SignOut size={16} weight="duotone" /> Logout
              </button>
            </div>
          ) : (
            <Link to="/login" className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-2 hover:bg-[#D62828] transition-colors text-sm" data-testid="nav-login">Sign In</Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <button className="md:hidden flex items-center gap-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-mobile-toggle">
          {user && unreadCount > 0 && (
            <span className="bg-[#FF3B30] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5">
              {unreadCount}
            </span>
          )}
          {menuOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-[#E4E4E7] py-4 px-6 flex flex-col gap-3 z-50" data-testid="nav-mobile-menu">
          <Link to="/quiz" onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wide py-2">Find Your Fit</Link>
          <Link to="/browse" onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wide py-2">Browse</Link>
          <Link to="/compare" onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wide py-2">Compare</Link>
          <Link to="/price-drops" onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wide py-2 flex items-center gap-2">
            Deals {dropCount > 0 && <span className="bg-[#FF3B30] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{dropCount}</span>}
          </Link>
          {user && <Link to="/profile" onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wide py-2 flex items-center gap-2 text-[#FF3B30]">
            <Sparkle size={14} weight="fill" /> My Profile
          </Link>}
          {user && <Link to="/wishlist" onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wide py-2 flex items-center gap-2">
            Wishlist {wishlistCount > 0 && <span className="bg-[#FF3B30] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{wishlistCount}</span>}
          </Link>}
          {user?.role === 'admin' && <Link to="/admin" onClick={() => setMenuOpen(false)} className="text-sm font-bold uppercase tracking-wide py-2">Dashboard</Link>}
          <select value={currency} onChange={(e) => changeCurrency(e.target.value)}
            className="border-2 border-[#E4E4E7] bg-white text-sm font-bold uppercase px-3 py-2 w-full" data-testid="currency-selector-mobile">
            {currencies.map(c => <option key={c} value={c}>{labels[c]}</option>)}
          </select>
          {user ? (
            <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="text-sm font-bold uppercase tracking-wide py-2 text-left text-[#FF3B30]">Logout</button>
          ) : (
            <Link to="/login" onClick={() => setMenuOpen(false)} className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-2 text-center text-sm">Sign In</Link>
          )}
        </div>
      )}
    </nav>
  );
}
